import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
} from '@angular/core';
import { MemberSearch } from '../member-search/member-search.model';
import { IdentityService } from '../services/identity.service';
import { MemberService } from '../services/member.service';
import { primarySelection } from 'src/app/shared/models/primary-selection.model';
import { GlobalService } from '../services/global.service';
import { Router } from '@angular/router';
import { isCallSignatureDeclaration } from 'typescript';
import { ExportService } from 'src/app/shared/services/export.service';
import { ICalendar } from 'src/app/admin/service-setup/iCalendar.model';
import { MemberSearchService } from '../member-search/member-search.service';

@Component({
    selector: 'app-user-tools',
    templateUrl: './user-tools.component.html',
    styleUrls: ['./user-tools.component.scss'],
    standalone: false
})
export class UserToolsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: MemberSearch;
  @Input() subdomain: string;
  @Input() showDefault: boolean;
  @Output() actionClicked = new EventEmitter();
  primarySelection: primarySelection[] = [];
  public isLoading:boolean = true;
  pendingPrimary: primarySelection = null;
  savingPrimary = false;
  contactsError = '';
  contactsSuccess = '';
  private contactsLoaded = false;
  private contactsVersion = 0;
  private destroyed = false;
  constructor(
    public globalService: GlobalService,
    public identityService: IdentityService,
    private memberService: MemberService,
    private _router: Router,
    private exportService: ExportService,
    private memberSearchService: MemberSearchService
  ) {}

  ngOnInit(): void {
    if (this.subdomain != null)
      this.subdomain = this.subdomain.trim().toLowerCase();
    this.isLoading = false;
  }

  ngOnChanges(): void {
    this.contactsVersion++; this.contactsLoaded = false; this.primarySelection = [];
    this.pendingPrimary = null; this.contactsError = ''; this.contactsSuccess = ''; this.savingPrimary = false;
    if (this.user) { this.user.expanded = false; this.user['processing'] = false; }
  }
  ngOnDestroy(): void { this.destroyed = true; this.contactsVersion++; }
  text(english: string, arabic: string): string { return this.globalService.isRTL ? arabic : english; }
  get primaryPrompt(): string {
    const name = this.pendingPrimary ? `${this.pendingPrimary.firstName} ${this.pendingPrimary.lastName}`.trim() : '';
    return this.text(`Make ${name} the primary contact for ${this.user?.fullName || this.user?.firstName}?`,
      `هل تريد تعيين ${name} كجهة الاتصال الرئيسية لـ ${this.user?.fullName || this.user?.firstName}؟`);
  }
  requestPrimary(contact: primarySelection): void {
    if (this.savingPrimary || contact.primary || this.user.fullName === this.user.primary ||
        !this.primarySelection.includes(contact)) return;
    this.pendingPrimary = contact;
  }
  async confirmPrimary(): Promise<void> {
    const contact = this.pendingPrimary, user = this.user, version = this.contactsVersion;
    if (!contact || this.savingPrimary) return;
    this.pendingPrimary = null; this.savingPrimary = true; this.contactsError = ''; this.contactsSuccess = '';
    try {
      const result = await this.memberService.setPrimarySelection(user.memberId, contact.userId);
      if (this.destroyed || version !== this.contactsVersion) return;
      if (!Array.isArray(result) || !result.some(item => item.userId === contact.userId && item.primary === true))
        throw new Error('Primary contact was not confirmed');
      this.primarySelection = result; user['primarySelection'] = result;
      user.userId = contact.userId; user.primary = `${contact.firstName} ${contact.lastName}`.trim();
      this.contactsSuccess = this.text('Primary contact updated.', 'تم تحديث جهة الاتصال الرئيسية.');
    } catch {
      if (!this.destroyed && version === this.contactsVersion)
        this.contactsError = this.text('We could not confirm the change. Please try again.', 'تعذر تأكيد التغيير. يرجى المحاولة مرة أخرى.');
    } finally { if (version === this.contactsVersion) this.savingPrimary = false; }
  }

  NoPrimary(selection: primarySelection[]): boolean {
    return selection.filter((a) => a.primary == true).length == 0;
  }

  async toggle(user: MemberSearch) {
    if (user['processing'] || this.savingPrimary) return;
    user.expanded = !user.expanded;
    if (!user.expanded || this.contactsLoaded) return;
    await this.loadContacts();
  }
  async loadContacts(): Promise<void> {
    const user = this.user, version = this.contactsVersion;
    if (user['processing']) return;
    user['processing'] = true; this.contactsError = '';
    try {
      const result = await this.memberService.getPrimarySelection(user.memberId);
      if (this.destroyed || version !== this.contactsVersion) return;
      if (!Array.isArray(result)) throw new Error('Missing contacts');
      this.primarySelection = result; user['primarySelection'] = result; this.contactsLoaded = true;
    } catch {
      if (!this.destroyed && version === this.contactsVersion)
        this.contactsError = this.text('Family contacts could not be loaded. Please try again.', 'تعذر تحميل جهات اتصال الأسرة. يرجى المحاولة مرة أخرى.');
    } finally { if (version === this.contactsVersion) user['processing'] = false; }
  }

  EditNote(member: number) {
    this._router.navigate(['/admin/notes/edit/' + member]);
  }

  OpenProfile(userId: string) {
    this._router.navigate(['/admin/edit-account/details/' + userId]);
  }

  OpenPoints(userId: string) {
    this._router.navigate(['/add-point/' + userId + '/0/']);
  }

  ViewNotes(member: number) {
    this._router.navigate(['/admin/notes/view/' + member]);
  }

  DonationHistory(user: MemberSearch) {
    if (user.memberId == 0)
      this._router.navigate(['/admin/donation/history/' + user.id]);
    else
      this._router.navigate(['/admin/donation/history/' + user.memberId]);
  }

  PossibleAccounts(memberId: number) {
    this._router.navigate(['/admin/donation/possible-accounts/' + memberId]);
  }

  followup(member: number) {
    this._router.navigate(['/admin/followup/details/' + member]);
  }

  editPermissions(userId: string)
  {
    this._router.navigate(['/admin/permissions/details/' + userId]);
  }

  attendance(member: number) {
    this._router.navigate(['/admin/attendance/details/' + member]);
  }

  attendanceChart(member: number) {
    this._router.navigate(['/admin/attendance/chart/' + member]);
  }

  phoneCall(phone: string) {
    location.href = 'tel: ' + phone;
    // window.open('tel: ' + phone);
    if (this.user.memberId > 0)
      this.actionClicked.emit({ type: 'Call', user: this.user });
  }

  sms(phone: string) {
    location.href = 'sms: ' + phone;
    // window.open('sms: ' + phone);
    if (this.user.memberId > 0)
      this.actionClicked.emit({ type: 'Text', user: this.user });
  }

  email(email: string) {
    location.href = 'mailto: ' + email;
    // window.open('mailto: ' + email);
    if (this.user.memberId > 0)
      this.actionClicked.emit({ type: 'Email', user: this.user });
  }

googleUrl(member: any) {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const url = isIOS
    ? `http://maps.apple.com/?saddr=Current%20Location&daddr=${encodeURIComponent(member.address)}`
    : `https://maps.google.com/?daddr=${encodeURIComponent(member.address)}`;

  window.location.href = url; // 👈 change here

  if (this.user.memberId > 0) {
    this.actionClicked.emit({ type: 'Visit', user: this.user });
  }
}

  MaskLast(name: string) {
    if (this.subdomain == 'demo') return name[0] + 'XXXXXXXXX';
    return name;
  }

  TriggerTask() {
    this.actionClicked.emit({ type: 'Visit', user: this.user });
  }

  AssignTask() {
    this.memberSearchService.assignMembers = [this.user.memberId];
    this._router.navigate(['/admin/assign/0']);
  }

  TriggerGroups(member: number)
  {
    this._router.navigate(['/admin/user-groups/' + member]);
  }

  TriggerTags(member: number)
  {
    this._router.navigate(['/admin/user-tags/' + member]);
  }

  ResetPrimarySelection() {
    this.primarySelection.map((a) => (a['expandFunctions'] = false));
  }

  ExpandFunctions(primary: any) {
    if (this.savingPrimary) return;
    if (
      primary['expandFunctions'] == null ||
      primary['expandFunctions'] == false
    ) {
      this.ResetPrimarySelection();
      primary['expandFunctions'] = true;
      this.primarySelection.find((a) => a.userId == primary.userId)[
        'expandFunctions'
      ] = true;
    } else primary['expandFunctions'] = false;
  }

  AddReminder(user: any) {
    let calendar: ICalendar = new ICalendar();
    let data: ICalendar[] = [];
    calendar.start = user.planDate;
    calendar.end = user.planDate;
    calendar.location = '';
    calendar.summary = user.activityStr;
    calendar.description =
      user.activityStr +
      ': ' +
      user.firstName +
      ' ' +
      user.lastName +
      ' - ' +
      user.destination;
    data.push(calendar);
    this.exportService.downloadICalendar(data, 'Calendar');
  }

CloseHelp() {
  console.log('Close fired');
  this.user['toolsHelp'] = false;
  console.log('toolsHelp=', this.user['toolsHelp']);
}
}
